/* eslint-disable @typescript-eslint/no-explicit-any */

import { mulberry32 } from '../hash/hash';

function pathValue<T extends { [key: string]: any }>(obj: T, path: string) {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

export function ArrayUniqueObjects<T extends { [key: string]: any }>(
  arr: Array<T>,
  keys: Array<string>,
  matchAllKeys = true,
): Array<T> {
  const unique: Array<T> = [];

  arr.forEach((item) => {
    const hasItem = unique.some((obj) => {
      const identicalKeys = keys.filter((key) => {
        const objValue = pathValue(obj, key);
        const itemValue = pathValue(item, key);

        return objValue === itemValue;
      });

      return matchAllKeys
        ? keys.length === identicalKeys.length
        : identicalKeys.length > 0;
    });

    if (!hasItem) {
      unique.push(item);
    }
  });

  return unique;
}

const shuffleIndexes = (
  array: Array<number>,
  seed: number,
  mayRetry = true,
  retry = 0,
): Array<number> => {
  const shuffled = [...array];
  const mulberryRandom = mulberry32(seed);

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(mulberryRandom() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  if (
    mayRetry &&
    retry < 10 &&
    JSON.stringify(array) === JSON.stringify(shuffled)
  ) {
    return shuffleIndexes(array, seed + 10, true, retry + 1);
  }

  return shuffled;
};

// Perform Fisher-Yates Shuffle using the seeded Mulberry32 PRNG
export function seededShuffle(
  array: Array<any>,
  seed: number,
  mayRetry = true,
  retry = 0,
): Array<any> {
  const shuffledIndexes = shuffleIndexes(
    array.map((value, index) => index),
    seed,
    mayRetry,
    retry,
  );

  return shuffledIndexes.map((index) => array[index]);
}
